import { BusinessError, EntityKind, ErrorKind, TiersAccountRestrictionListSchema, TiersLinkedAccountListExpandedSchema, } from '@mgdis/eu-domain';
export class TiersAccountRestAdapter {
    restHttpAdapter;
    tenantId;
    constructor({ tenantId, restHttpAdapter }) {
        this.tenantId = tenantId;
        this.restHttpAdapter = restHttpAdapter;
    }
    async listLinkedAccount(tiersReference) {
        try {
            if (!tiersReference) {
                return {
                    error: new BusinessError(ErrorKind.NOT_FOUND, {
                        entity: EntityKind.TIERS,
                        href: tiersReference,
                        message: `[listLinkedAccount] tiersReference is mandatory`,
                    }),
                };
            }
            const res = await this.restHttpAdapter.get(`/gestion-depot-demandes/${this.tenantId}/tiers/${tiersReference}/linkedUsers`);
            const result = await TiersLinkedAccountListExpandedSchema.safeParseAsync(res.data);
            if (!result.success) {
                return {
                    error: new BusinessError(ErrorKind.TECHNICAL_ERROR, {
                        entity: EntityKind.TIERS,
                        href: tiersReference,
                        message: `[listLinkedAccount] Error parsing linkedUsersResult. ${result.error.message}`,
                    }),
                };
            }
            return { response: result.data };
        }
        catch (error) {
            return {
                error: new BusinessError(ErrorKind.TECHNICAL_ERROR, {
                    entity: EntityKind.TIERS,
                    href: tiersReference,
                    message: error instanceof Error ? error.message : 'Unknown error',
                }),
            };
        }
    }
    async listAccountsByEmail(email) {
        try {
            if (!email) {
                return {
                    error: new BusinessError(ErrorKind.NOT_FOUND, {
                        entity: EntityKind.TIERS,
                        message: `[listAccountsByEmail] email is mandatory`,
                    }),
                };
            }
            const res = await this.restHttpAdapter.get(`/gestion-depot-demandes/${this.tenantId}/users`, {
                params: {
                    $filter: `emails eq '${email.replaceAll("'", '')}'`,
                },
            });
            const users = [];
            if (Array.isArray(res?.data)) {
                for (const user of res.data) {
                    if (typeof user?.self === 'string') {
                        users.push(user.self);
                    }
                }
            }
            return { response: users };
        }
        catch (error) {
            return {
                error: new BusinessError(ErrorKind.TECHNICAL_ERROR, {
                    entity: EntityKind.NO_BUSINESS_ENTITY,
                    message: error instanceof Error ? error.message : 'Unknown error',
                }),
            };
        }
    }
    /**
     * Attach an account to tiers
     **/
    async attachUserToTiers(tiersReference, email) {
        try {
            if (!email || !tiersReference) {
                return {
                    error: new BusinessError(ErrorKind.NOT_FOUND, {
                        entity: EntityKind.TIERS,
                        href: tiersReference,
                        message: `[attachUserToTiers] tiersReference and email are mandatory`,
                    }),
                };
            }
            await this.restHttpAdapter.post(`/gestion-depot-demandes/${this.tenantId}/tiers/${tiersReference}/rattachements`, {
                email: email,
            });
            return { response: null };
        }
        catch (error) {
            return {
                error: new BusinessError(ErrorKind.TECHNICAL_ERROR, {
                    entity: EntityKind.TIERS,
                    href: tiersReference,
                    message: error instanceof Error ? error.message : 'Unknown error',
                }),
            };
        }
    }
    /**
     * Attach the user to the tiers if the account exsits. Otherwise, it will send an email to the user. After creating his account, the account will be automatically attached to the tiers
     **/
    async attachOrInviteUserToTiers(tiersReference, email) {
        try {
            if (!email) {
                return {
                    error: new BusinessError(ErrorKind.NOT_FOUND, {
                        entity: EntityKind.TIERS,
                        href: tiersReference,
                        message: `[attachOrInviteUserToTiers] email is mandatory`,
                    }),
                };
            }
            await this.restHttpAdapter.post(`/gestion-depot-demandes/${this.tenantId}/tiers/${tiersReference}/autorattachement`, {
                email: email,
            });
            return { response: null };
        }
        catch (error) {
            return {
                error: new BusinessError(ErrorKind.TECHNICAL_ERROR, {
                    entity: EntityKind.TIERS,
                    href: tiersReference,
                    message: error instanceof Error ? error.message : 'Unknown error',
                }),
            };
        }
    }
    async detachAccount(tiersReference, accountHref) {
        try {
            if (!accountHref || !tiersReference) {
                return {
                    error: new BusinessError(ErrorKind.NOT_FOUND, {
                        entity: EntityKind.TIERS,
                        href: tiersReference,
                        message: `[detachAccount] tiersReference, accountHref are mandatory`,
                    }),
                };
            }
            await this.restHttpAdapter.post(`/referentiel-tiers/api/tenants/${this.tenantId}/tiers/${tiersReference}/linked-users/detach`, {
                href: accountHref,
            });
            return { response: null };
        }
        catch (error) {
            return {
                error: new BusinessError(ErrorKind.TECHNICAL_ERROR, {
                    entity: EntityKind.TIERS,
                    href: tiersReference,
                    message: error instanceof Error ? error.message : 'Unknown error',
                }),
            };
        }
    }
    async listTiersAccountRestrictions() {
        try {
            const res = await this.restHttpAdapter.get(`/referentiel-financement/api/tenants/${this.tenantId}/demandes-apprenants/restrictions`);
            const result = await TiersAccountRestrictionListSchema.safeParseAsync(res.data);
            if (!result.success) {
                return {
                    error: new BusinessError(ErrorKind.TECHNICAL_ERROR, {
                        entity: EntityKind.TIERS,
                        message: `[listTiersAccountRestrictions] Error parsing linkedUsersResult. ${result.error.message}`,
                    }),
                };
            }
            return { response: result.data.usersRestrictions };
        }
        catch (error) {
            return {
                error: new BusinessError(ErrorKind.TECHNICAL_ERROR, {
                    entity: EntityKind.DEMANDE_APPRENANT,
                    message: error instanceof Error ? error.message : 'Unknown error',
                }),
            };
        }
    }
    async updateTiersAccountRestrictions(tiersHref, restrictions) {
        try {
            if (!tiersHref) {
                return {
                    error: new BusinessError(ErrorKind.NOT_FOUND, {
                        entity: EntityKind.TIERS,
                        href: tiersHref,
                        message: `[updateTiersAccountRestrictions] tiersReference are mandatory`,
                    }),
                };
            }
            const body = {
                tiersId: tiersHref,
                usersRestrictions: restrictions,
            };
            const res = await this.restHttpAdapter.put(`/referentiel-financement/api/tenants/${this.tenantId}/demandes-apprenants/restrictions`, body);
            const result = await TiersAccountRestrictionListSchema.safeParseAsync(res.data);
            if (!result.success) {
                return {
                    error: new BusinessError(ErrorKind.TECHNICAL_ERROR, {
                        entity: EntityKind.TIERS,
                        message: `[updateTiersAccountRestrictions] Error parsing linkedUsersResult. ${result.error.message}`,
                    }),
                };
            }
            return { response: result.data.usersRestrictions };
        }
        catch (error) {
            console.error('[tiers-account-rest.adapter] updateTiersAccountRestrictions', error);
            return {
                error: new BusinessError(ErrorKind.TECHNICAL_ERROR, {
                    entity: EntityKind.DEMANDE_APPRENANT,
                    message: error instanceof Error ? error.message : 'Unknown error',
                }),
            };
        }
    }
}
//# sourceMappingURL=tiers-account-rest.adapter.js.map